import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { seconds } from '~/interfaces';
import { VideoState } from '~/modules/Elements/VideoPlayer/VideoState';
import type { LoadVideByIdOpts, VideoPlayerRef } from './Youtube';
import type { VideoPlayerProps } from './index';

function buildEmbedUrl(videoId: string, autoplay?: boolean, startAt?: number) {
    const url = new URL(`https://www.dailymotion.com/embed/video/${videoId}`);

    if (autoplay) {
        url.searchParams.set('autoplay', '1');
    }

    const start = Number(startAt);
    if (Number.isFinite(start) && start > 0) {
        url.searchParams.set('start', String(Math.floor(start)));
    }

    return url.toString();
}

function normalizeStartAt(startAt?: number) {
    const n = Number(startAt);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

export default forwardRef(function DailymotionEmbed(
    { video, autoplay, controls, startAt, width, height, onStateChange }: VideoPlayerProps,
    ref: ForwardedRef<VideoPlayerRef>,
) {
    const safeStartAt = useMemo(() => normalizeStartAt(startAt), [startAt]);

    const [iframeSrc, setIframeSrc] = useState<string>(() => buildEmbedUrl(video, autoplay, safeStartAt));
    const [status, setStatus] = useState<VideoState>(autoplay ? VideoState.PLAYING : VideoState.CUED);

    // reloj local aproximado para no romper del todo el engine
    const [playing, setPlaying] = useState<boolean>(Boolean(autoplay));
    const [clockBase, setClockBase] = useState<number>(safeStartAt);
    const startedAtRef = useRef<number | null>(autoplay ? Date.now() : null);

    useEffect(() => {
        setIframeSrc(buildEmbedUrl(video, autoplay, safeStartAt));
        setClockBase(safeStartAt);
        setPlaying(Boolean(autoplay));
        startedAtRef.current = autoplay ? Date.now() : null;
        setStatus(autoplay ? VideoState.PLAYING : VideoState.CUED);
        onStateChange?.(autoplay ? VideoState.PLAYING : VideoState.CUED);
    }, [video, autoplay, safeStartAt, onStateChange]);

    function getApproxCurrentTime() {
        if (!playing || startedAtRef.current === null) return clockBase;
        const elapsed = (Date.now() - startedAtRef.current) / 1000;
        return Math.max(0, clockBase + elapsed);
    }

    function reloadIframe(nextStart: number, nextAutoplay: boolean) {
        setIframeSrc(buildEmbedUrl(video, nextAutoplay, nextStart));
    }

    useImperativeHandle(ref, () => ({
        getStatus: () => status,

        setSize: () => {
            // no-op: el tamaño lo controlas con props
        },

        seekTo: (timeSec: seconds) => {
            const next = Math.max(0, Number(timeSec) || 0);
            setClockBase(next);
            startedAtRef.current = playing ? Date.now() : null;
            reloadIframe(next, playing);
        },

        setPlaybackSpeed: () => {
            // no soportado con iframe básico
        },

        setVolume: () => {
            // no soportado con iframe básico
        },

        getCurrentTime: async () => getApproxCurrentTime(),

        loadVideoById: (opts: LoadVideByIdOpts) => {
            const next = Math.max(0, Number(opts.startSeconds) || 0);
            setClockBase(next);
            setPlaying(true);
            startedAtRef.current = Date.now();
            setStatus(VideoState.PLAYING);
            onStateChange?.(VideoState.PLAYING);
            setIframeSrc(buildEmbedUrl(opts.videoId, true, next));
        },

        playVideo: () => {
            const current = getApproxCurrentTime();
            setClockBase(current);
            startedAtRef.current = Date.now();
            setPlaying(true);
            setStatus(VideoState.PLAYING);
            onStateChange?.(VideoState.PLAYING);
            reloadIframe(current, true);
        },

        pauseVideo: () => {
            const current = getApproxCurrentTime();
            setClockBase(current);
            startedAtRef.current = null;
            setPlaying(false);
            setStatus(VideoState.PAUSED);
            onStateChange?.(VideoState.PAUSED);

            // OJO: el iframe básico no se puede pausar de verdad sin API del player.
            // Aquí solo pausamos el "reloj" interno.
        },

        getDuration: async () => 0,
    }));

    return (
        <iframe
            title={`Dailymotion ${video}`}
            src={iframeSrc || undefined}
            width={width}
            height={height}
            frameBorder={0}
            allow="autoplay; fullscreen; picture-in-picture; web-share"
            allowFullScreen
            style={{ border: 0, background: 'black' }}
            data-controls={controls ? 'true' : 'false'}
        />
    );
});